import React, { useEffect, useState } from 'react';
import * as echarts from 'echarts';
import { useAuth0 } from '@auth0/auth0-react';
import { startOfWeek, endOfWeek, parseISO, format } from 'date-fns';

interface ConversationCountChartProps {
  range: number;
  start_date: string | null;
  end_date: string | null;
  channel: string;
  setLoading: (loading: boolean) => void;
  getReports: (
    type?: string,
    lookback?: number,
    series?: string | null,
    channel?: string | null,
    startDate?: string | null,
    endDate?: string | null,
    downloadType?: string | null,
    tags?: string | null,
    seriesType?: string | null,
    seriesTagType?: string | null
  ) => Promise<any>;
}

interface SeriesItem {
  label: string;
  data: number[];
}

interface ChartData {
  labels: string[];
  data: SeriesItem[];
  result_count: number;
  error?: string;
}

interface WeekRanges {
  [weekRange: string]: {
    [label: string]: number;
  };
}

const ConversationCountChart: React.FC<ConversationCountChartProps> = ({
  range,
  start_date,
  end_date,
  channel,
  setLoading,
  getReports,
}) => {
  const { user } = useAuth0();
  const [data, setData] = useState<ChartData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resultCount, setResultCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const inFlightRef = React.useRef(false);

  const getConversationCount = async (
    type: string,
    lookback: number | null,
    channel: string | null,
    startDate: string | null,
    endDate: string | null
  ): Promise<void> => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      setLoading(true);
      setIsLoading(true);
      const getData: ChartData = await getReports(
        type,
        lookback ?? undefined,
        null,
        channel,
        startDate,
        endDate,
        null,
        null,
        null,
        null
      );
      if (getData.error) {
        setErrorMessage(getData.error);
        setData(null);
        setResultCount(0);
      } else {
        setErrorMessage(null);
        setData(getData);
        setResultCount(getData.result_count);
      }
      setLoading(false);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
      setIsLoading(false);
    } finally {
      inFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (!user) return;
    getConversationCount(
      'conversation_count',
      range,
      channel,
      start_date,
      end_date
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, range, channel, start_date, end_date]);

  useEffect(() => {
    if (!data) return;

    const chartDom = document.getElementById('conv-chart');
    if (!chartDom) return;

    let myChart = echarts.getInstanceByDom(chartDom);
    if (!myChart) {
      myChart = echarts.init(chartDom);
    }
  }, [data]);

  useEffect(() => {
    if (!data) return;

    const chartDom = document.getElementById('conv-chart');
    if (!chartDom) return;

    const myChart =
      echarts.getInstanceByDom(chartDom) || echarts.init(chartDom);

    const jsonData = data;
    
    // Safety check: ensure data has the expected structure
    if (!jsonData.labels || !Array.isArray(jsonData.labels) || 
        !jsonData.data || !Array.isArray(jsonData.data)) {
      console.warn('Invalid data structure:', jsonData);
      return;
    }

    let labels: string[];
    let seriesData: echarts.SeriesOption[];

    if (range === 30) {
      labels = jsonData.labels.map((label: string) =>
        format(parseISO(label), 'yyyy-MM-dd')
      );
      seriesData = jsonData.data.map((item: SeriesItem) => {
        return {
          name: item.label,
          type: 'bar',
          stack: 'total',
          data: item.data,
        };
      });
    } else {
      const weekRanges: WeekRanges = {};

      jsonData.labels.forEach((label: string, index: number) => {
        const date = parseISO(label);
        const startOfWeekDate = startOfWeek(date);
        const endOfWeekDate = endOfWeek(date);
        const weekRange = `${format(startOfWeekDate, 'MMM d')} - ${format(endOfWeekDate, 'd')}`;

        if (!weekRanges[weekRange]) {
          weekRanges[weekRange] = {};
        }

        jsonData.data.forEach((item: SeriesItem) => {
          if (!weekRanges[weekRange][item.label]) {
            weekRanges[weekRange][item.label] = 0;
          }
          weekRanges[weekRange][item.label] += item.data[index];
        });
      });

      labels = Object.keys(weekRanges);
      seriesData = jsonData.data.map((item: SeriesItem) => {
        return {
          name: item.label,
          type: 'bar',
          stack: 'total',
          data: labels.map((week: string) => weekRanges[week][item.label] || 0),
        };
      });
    }

    // Brand-aware color palette
    const toRgb = (hex: string) => {
      const h = hex.replace('#', '').trim();
      const s =
        h.length === 3
          ? h
              .split('')
              .map(c => c + c)
              .join('')
          : h;
      const bigint = parseInt(s, 16);
      return {
        r: (bigint >> 16) & 255,
        g: (bigint >> 8) & 255,
        b: bigint & 255,
      };
    };
    const rgbToHsl = ({ r, g, b }: { r: number; g: number; b: number }) => {
      r /= 255;
      g /= 255;
      b /= 255;
      const max = Math.max(r, g, b),
        min = Math.min(r, g, b);
      let h = 0,
        s = 0;
      const l = (max + min) / 2;
      if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max - min);
        switch (max) {
          case r:
            h = (g - b) / d + (g < b ? 6 : 0);
            break;
          case g:
            h = (b - r) / d + 2;
            break;
          case b:
            h = (r - g) / d + 4;
            break;
        }
        h /= 6;
      }
      return {
        h: Math.round(h * 360),
        s: Math.round(s * 100),
        l: Math.round(l * 100),
      };
    };
    const getBrandPrimary = () => {
      try {
        const css = getComputedStyle(document.documentElement);
        const val = css.getPropertyValue('--primary-color').trim();
        return val || '#e83d56';
      } catch (e) {
        return '#e83d56';
      }
    };
    const generateBrandPalette = (count: number): string[] => {
      const baseHex = getBrandPrimary();
      const { h, s, l } = rgbToHsl(toRgb(baseHex));
      const colors: string[] = [];

      if (count <= 6) {
        const hueOffsets = [0, 180, 90, -90, 45, -45];
        for (let i = 0; i < count; i++) {
          const offset = hueOffsets[i % hueOffsets.length];
          const light = i % 2 === 0 ? l : Math.min(70, Math.max(40, l + 20));
          colors.push(
            `hsl(${(h + offset + 360) % 360}, ${Math.min(90, Math.max(60, s))}%, ${light}%)`
          );
        }
      } else {
        const primaryColors = [
          baseHex,
          '#2563eb',
          '#10b981',
          '#f59e0b',
          '#8b5cf6',
          '#ef4444',
          '#06b6d4',
          '#f97316',
          '#84cc16',
          '#ec4899',
          '#6366f1',
          '#14b8a6',
        ];

        for (let i = 0; i < Math.min(count, primaryColors.length); i++) {
          colors.push(primaryColors[i]);
        }

        if (count > primaryColors.length) {
          const goldenRatio = 0.618033988749895;
          let hue = h / 360;
          for (let i = primaryColors.length; i < count; i++) {
            hue = (hue + goldenRatio) % 1;
            const saturation = 70 + (i % 3) * 10;
            const lightness = 50 + ((i % 4) - 2) * 10;
            colors.push(
              `hsl(${Math.round(hue * 360)}, ${saturation}%, ${lightness}%)`
            );
          }
        }
      }
      return colors;
    };

    const option: echarts.EChartsOption = {
      color: generateBrandPalette(seriesData.length),
      tooltip: {
        trigger: 'item',
        axisPointer: {
          type: 'shadow',
        },
        formatter: function (params: any) {
          return `${params.name}<br/>${params.seriesName}: ${params.value}`;
        },
      },
      legend: {
        data: seriesData.map((item: any) => item.name),
        type: 'scroll',
        orient: 'horizontal',
        bottom: 10,
      },
      grid: {
        left: '1%',
        right: '4%',
        bottom: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: labels,
      },
      yAxis: {
        type: 'value',
      },
      series: seriesData,
    };

    myChart.clear();
    myChart.setOption(option, true);
    myChart.resize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, range]);

  // Dispose on unmount
  useEffect(() => {
    return () => {
      const chartDom = document.getElementById('conv-chart');
      if (!chartDom) return;
      const inst = echarts.getInstanceByDom(chartDom);
      if (inst && !inst.isDisposed()) inst.dispose();
    };
  }, []);

  // Add window resize listener
  useEffect(() => {
    const handleResize = () => {
      const chartDom = document.getElementById('conv-chart');
      if (chartDom) {
        const myChart = echarts.getInstanceByDom(chartDom);
        if (myChart && !myChart.isDisposed()) {
          myChart.resize();
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className="report-box" style={{ position: 'relative' }}>
      <h2>Conversation Count</h2>
      {resultCount === 0 && !isLoading && (
        <div className="no-data">
          A chart of the conversation counts will show up here once you have
          conversations in your channels.
        </div>
      )}
      {errorMessage && <div className="error-message">{errorMessage}</div>}
      <div id="conv-chart" style={{ height: 400, width: '100%' }}></div>

      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 60,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10,
          }}
        >
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: '3rem', height: '3rem' }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationCountChart;

